<template>
  <div class="old-designer-container">
    <!-- 顶部 Tab 栏 -->
    <div class="old-designer-header">
      <div class="header-left">
        <span class="header-title">{{ flowName }}</span>
        <span class="header-sub">经典设计器</span>
      </div>
      <div class="header-center">
        <div class="form-tabs">
          <button
            v-for="(f, i) in formList"
            :key="f._key"
            class="ftab"
            :class="{ active: i === activeFormIndex }"
            @click="switchForm(i)"
            :title="f.formName"
          >
            <template v-if="editingTabIndex === i">
              <input
                class="ftab-edit-input"
                v-model="editingTabName"
                @blur="finishRenameTab(i)"
                @keydown.enter="finishRenameTab(i)"
                @keydown.escape="cancelRenameTab"
                @click.stop
              />
            </template>
            <span v-else class="ftab-label" @dblclick.stop="startRenameTab(i, f.formName)">{{ f.formName }}</span>
            <span class="ftab-badge" v-if="f.formType === '2'">新</span>
            <span class="ftab-badge old" v-else>旧</span>
          </button>
        </div>
      </div>
      <div class="header-right">
        <button class="btn-save" @click="handleSaveForm"><i class="fas fa-save"></i> 保存</button>
        <button class="btn-back" @click="goBack"><i class="fas fa-arrow-left"></i> 返回</button>
      </div>
    </div>

    <!-- 主体 -->
    <div class="old-designer-body" v-if="activeForm">
      <!-- 新设计器表单：仅预览 -->
      <div v-if="activeForm.formType === '2'" class="new-form-preview">
        <div class="preview-toolbar">
          <span class="preview-hint"><i class="fas fa-lock"></i> 新设计器表单，仅支持预览和改名。请到"新设计器"中编辑。</span>
        </div>
        <div v-if="!previewControls.length" class="preview-empty">
          <i class="fas fa-file-alt"></i>
          <p>此表单暂无可预览的控件</p>
        </div>
        <div v-else class="preview-form-body">
          <div
            v-for="ctrl in previewControls"
            :key="ctrl.id"
            class="preview-cell"
            :style="previewCellStyle(ctrl)"
          >
            <ControlRenderer :control="ctrl" selected-id="" />
          </div>
        </div>
      </div>

      <!-- 旧设计器表单：iframe 编辑器 -->
      <div v-else class="old-designer-iframe-container">
        <iframe
          ref="iframeRef"
          :src="iframeSrc"
          class="designer-iframe"
          frameborder="0"
          @load="onIframeLoad"
        ></iframe>
      </div>
    </div>

    <div v-else class="designer-loading"><i class="fas fa-spinner fa-spin"></i> 加载中...</div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick } from 'vue'
import { getFormsByFlow, saveForm } from '../../api/form'
import ControlRenderer from './FormDesigner/ControlRenderer.vue'

const props = defineProps({
  tabPayload: { type: Object, default: () => ({}) }
})

const flowGuid = computed(() => props.tabPayload?.flowGuid || window.currentEditingProcess?.guid || '')
const flowName = computed(() => props.tabPayload?.flowName || window.currentEditingProcess?.name || '')

// 表单列表
const formList = reactive([])
const activeFormIndex = ref(0)
const activeForm = computed(() => formList[activeFormIndex.value] || null)

// 预览控件（新设计器表单的 JSON 控件解析结果）
const previewControls = computed(() => {
  const f = activeForm.value
  if (!f || f.formType !== '2') return []
  try {
    return JSON.parse(f.formContent || '[]')
  } catch { return [] }
})

function previewCellStyle(ctrl) {
  const cs = (ctrl.props && ctrl.props.colSpan) || 12
  return { flex: '0 0 ' + (cs / 12 * 100).toFixed(2) + '%', maxWidth: (cs / 12 * 100).toFixed(2) + '%' }
}

// iframe
const iframeRef = ref(null)
const iframeSrc = computed(() => {
  const guid = flowGuid.value
  const name = encodeURIComponent(flowName.value)
  return `./formBuilder/formBuilder.htm?flowGuid=${guid}&flowName=${name}`
})

function onIframeLoad() {
  console.log('[OldFormDesigner] iframe loaded, flowGuid:', flowGuid.value)
}

// Tab 重命名
const editingTabIndex = ref(-1)
const editingTabName = ref('')

function startRenameTab(index, currentName) {
  editingTabIndex.value = index
  editingTabName.value = currentName
  nextTick(() => {
    const inputs = document.querySelectorAll('.ftab-edit-input')
    if (inputs.length > 0) { inputs[inputs.length - 1].focus(); inputs[inputs.length - 1].select() }
  })
}

function finishRenameTab(index) {
  const newName = editingTabName.value.trim()
  if (newName && formList[index]) {
    formList[index].formName = newName
    formList[index]._dirty = true
  }
  editingTabIndex.value = -1
  editingTabName.value = ''
}

function cancelRenameTab() {
  editingTabIndex.value = -1
  editingTabName.value = ''
}

// 表单切换
let _formKey = 0
function switchForm(index) {
  if (index === activeFormIndex.value) return
  activeFormIndex.value = index
}

// 保存（仅用于改名后的保存）
async function handleSaveForm() {
  const dirtyForms = formList.filter(f => f._dirty)
  if (!dirtyForms.length) {
    window.$message.info('没有需要保存的修改')
    return
  }
  try {
    for (const f of dirtyForms) {
      const payload = {
        Guid: f.guid || '',
        FlowGuid: flowGuid.value,
        FormName: f.formName,
        FormContent: f.formContent || '',
        JSContent: f.jsContent || '',
        FormType: f.formType
      }
      await saveForm(payload)
    }
    formList.forEach(f => { f._dirty = false })
    window.$message.success('保存成功！')
  } catch (e) {
    window.$message.error('保存失败: ' + (e.message || e))
  }
}

function goBack() {
  window.dispatchEvent(new Event('navigateToProcessManagement'))
}

// 加载表单列表
async function loadForms() {
  if (!flowGuid.value) return
  try {
    const res = await getFormsByFlow(flowGuid.value)
    const data = res?.data || res?.Data || res
    const list = data?.data || data?.Data || data?.DataList || data
    if (Array.isArray(list) && list.length > 0) {
      formList.length = 0
      list.forEach(f => {
        formList.push({
          _key: ++_formKey,
          guid: f.guid ?? f.Guid ?? '',
          formName: f.formName ?? f.FormName ?? '未命名表单',
          formType: String(f.formType ?? f.FormType ?? '0'),
          formContent: f.formContent ?? f.FormContent ?? '',
          jsContent: f.jsContent ?? f.JSContent ?? '',
          _dirty: false
        })
      })
      activeFormIndex.value = 0
    }
  } catch (e) {
    console.warn('[OldFormDesigner] 加载表单失败:', e)
  }
}

onMounted(() => {
  loadForms()
})
</script>

<style scoped>
.old-designer-container { display: flex; flex-direction: column; height: calc(100vh - 110px); background: #fefce8; }

/* Header */
.old-designer-header { display: flex; align-items: center; padding: 0 15px; height: 50px; background: #fff; border-bottom: 1px solid #fde68a; flex-shrink: 0; gap: 12px; }
.header-left { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.header-title { font-size: 14px; font-weight: 600; color: #78350f; white-space: nowrap; }
.header-sub { font-size: 10px; color: #f59e0b; }
.header-center { flex: 1; display: flex; min-width: 0; }
.header-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.btn-back { display: flex; align-items: center; gap: 4px; padding: 5px 10px; border: 1px solid #fde68a; border-radius: 4px; background: #fff; font-size: 12px; color: #92400e; cursor: pointer; }
.btn-back:hover { background: #fffbeb; }
.btn-save { display: flex; align-items: center; gap: 4px; padding: 6px 16px; border: none; border-radius: 4px; background: #f59e0b; font-size: 13px; color: #fff; cursor: pointer; }
.btn-save:hover { background: #d97706; }

/* Tabs */
.form-tabs { display: flex; align-items: center; gap: 2px; overflow-x: auto; flex: 1; min-width: 0; }
.form-tabs::-webkit-scrollbar { height: 3px; }
.form-tabs::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 2px; }
.ftab { display: flex; align-items: center; gap: 4px; padding: 4px 10px; border: 1px solid #e5e7eb; border-radius: 4px 4px 0 0; background: #f3f4f6; font-size: 12px; color: #6b7280; cursor: pointer; white-space: nowrap; flex-shrink: 0; }
.ftab:hover { background: #e5e7eb; }
.ftab.active { background: #fff; color: #d97706; font-weight: 500; border-bottom-color: #fff; }
.ftab-label { max-width: 120px; overflow: hidden; text-overflow: ellipsis; }
.ftab-badge { font-size: 9px; padding: 1px 4px; border-radius: 3px; background: #10b981; color: #fff; }
.ftab-badge.old { background: #f59e0b; }
.ftab-edit-input { width: 80px; padding: 1px 4px; border: 1px solid #f59e0b; border-radius: 3px; font-size: 12px; outline: none; background: #fff; color: #374151; }

/* Body */
.old-designer-body { display: flex; flex: 1; overflow: hidden; }
.designer-loading { flex: 1; display: flex; align-items: center; justify-content: center; color: #9ca3af; font-size: 14px; gap: 8px; }

/* Iframe */
.old-designer-iframe-container { width: 100%; height: 100%; overflow: hidden; }
.designer-iframe { width: 100%; height: 100%; border: none; }

/* New form preview */
.new-form-preview { flex: 1; display: flex; flex-direction: column; overflow: hidden; }
.preview-toolbar { flex-shrink: 0; padding: 6px 16px; background: #fffbeb; border-bottom: 1px solid #fde68a; }
.preview-hint { font-size: 12px; color: #92400e; display: flex; align-items: center; gap: 6px; }
.preview-hint i { color: #f59e0b; }
.preview-empty { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; color: #9ca3af; }
.preview-empty i { font-size: 48px; margin-bottom: 12px; opacity: 0.4; }
.preview-empty p { font-size: 14px; }
.preview-form-body { flex: 1; overflow-y: auto; padding: 20px; display: flex; flex-wrap: wrap; align-items: flex-start; background: #fff; }
.preview-cell { padding: 10px 12px; box-sizing: border-box; }
</style>
